import React, { useState, useEffect } from 'react';
import { X, AlertCircle, RefreshCw, CheckCircle2, ShieldAlert, CreditCard } from 'lucide-react';
import { auth } from '../firebase';
import { MembershipTier } from '../types';
import { CollapsibleSection } from './common/CollapsibleSection';

interface SubscriptionCancelModalProps {
  tier: MembershipTier | 'AI_PERSONALIZATION_ADDON' | 'CHAT_PREDICTION_ADDON' | 'API_ACCESS_ADDON' | 'OPTIONS_ANALYTICS_ADDON' | 'addon_agent_standard' | 'addon_agent_pro' | 'addon_agent_vip' | 'addon_smart_order_standard' | 'addon_smart_order_pro' | 'addon_smart_order_vip';
  subId?: string;
  uiLanguage?: 'ko' | 'en';
  onClose: () => void;
  onCancelled: () => void;
  showToast?: (message: string, type?: 'success' | 'error' | 'info') => void;
}

export function SubscriptionCancelModal({
  tier,
  subId,
  uiLanguage = 'ko',
  onClose,
  onCancelled,
  showToast
}: SubscriptionCancelModalProps) {
  const notify = (msg: string, type: 'success' | 'error' | 'info' = 'info') => {
    if (showToast) showToast(msg, type);
    else alert(msg);
  };

  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<any>(null);
  const [cancelType, setCancelType] = useState<'IMMEDIATE_REFUND' | 'AUTO_RENEW_OFF'>('IMMEDIATE_REFUND');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isNoticeOpen, setIsNoticeOpen] = useState(false);

  useEffect(() => {
    setIsNoticeOpen(false);
    let isMounted = true;
    const fetchPreview = async () => {
      setLoading(true);
      try {
        const token = await auth.currentUser?.getIdToken();
        if (!token) throw new Error('인증 토큰이 없습니다.');

        const params = new URLSearchParams();
        if (subId) params.append('subId', subId);
        if (tier) params.append('tier', tier);

        const res = await fetch(`/api/payment/subscription/preview-cancel?${params.toString()}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await res.json() as any;
        if (isMounted) {
          if (res.ok) {
            setPreview(data);
          } else {
            notify(data.error || '환불 정보를 불러오지 못했습니다.', 'error');
          }
        }
      } catch (err: any) {
        console.error('Fetch cancel preview failed:', err);
        if (isMounted) notify('환불 정보 조회 실패', 'error');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchPreview();
    return () => { isMounted = false; };
  }, [tier, subId]);

  const handleExecuteCancel = async () => {
    setIsSubmitting(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('인증 토큰이 없습니다.');

      const res = await fetch('/api/payment/subscription/cancel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          subId: preview?.subId || subId,
          tier,
          cancelType,
          reason: reason || (cancelType === 'IMMEDIATE_REFUND' ? '사용자 요청 중도 해지 환불' : '사용자 요청 정기 결제 해지')
        })
      });

      const data = await res.json() as any;
      if (!res.ok) {
        throw new Error(data.error || '해지 처리 중 오류가 발생했습니다.');
      }

      notify(
        data.message || (cancelType === 'IMMEDIATE_REFUND' ? '구독 중도 해지 및 환불 처리가 완료되었습니다.' : '정기 결제가 성공적으로 해지되었습니다.'),
        'success'
      );
      onCancelled();
      onClose();
    } catch (err: any) {
      console.error('Execute cancel error:', err);
      notify(err.message || '해지 처리 중 오류가 발생했습니다.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getProductName = () => {
    if (tier === 'addon_agent_standard') return uiLanguage === 'ko' ? '시그널 레이더 Standard' : 'Signal Radar Standard';
    if (tier === 'addon_agent_pro') return uiLanguage === 'ko' ? '시그널 레이더 Pro' : 'Signal Radar Pro';
    if (tier === 'addon_agent_vip') return uiLanguage === 'ko' ? '시그널 레이더 VIP' : 'Signal Radar VIP';
    if (tier === 'addon_smart_order_standard') return uiLanguage === 'ko' ? '스마트 주문 Standard' : 'Smart Order Standard';
    if (tier === 'addon_smart_order_pro') return uiLanguage === 'ko' ? '스마트 주문 Pro' : 'Smart Order Pro';
    if (tier === 'addon_smart_order_vip') return uiLanguage === 'ko' ? '스마트 주문 VIP' : 'Smart Order VIP';
    if (tier === 'OPTIONS_ANALYTICS_ADDON') return uiLanguage === 'ko' ? '옵션/공매도 심층 분석 부가상품' : 'Options & Shorts Analytics Add-on';
    if (tier === 'AI_PERSONALIZATION_ADDON') return uiLanguage === 'ko' ? 'AI 개인화 부가상품' : 'AI Personalization Add-on';
    if (tier === 'CHAT_PREDICTION_ADDON') return uiLanguage === 'ko' ? 'AI 챗 플러스 퀀트 분석기 부가상품' : 'AI Chat Plus Quant Analytics Add-on';
    if (tier === 'API_ACCESS_ADDON') return uiLanguage === 'ko' ? 'Developer JSON Raw Data API 부가상품' : 'Developer JSON Raw Data API Add-on';
    if (tier === 'TIER1') return uiLanguage === 'ko' ? 'Tier 1 (주린이) 멤버십' : 'Tier 1 (Beginner) Tier';
    if (tier === 'TIER2') return uiLanguage === 'ko' ? 'Tier 2 (새싹 투자자) 멤버십' : 'Tier 2 (Novice) Tier';
    if (tier === 'TIER3') return uiLanguage === 'ko' ? 'Tier 3 (시장 참여자) 멤버십' : 'Tier 3 (Intermediate) Tier';
    if (tier === 'TIER4') return uiLanguage === 'ko' ? 'Tier 4 (전략가) 멤버십' : 'Tier 4 (Advanced) Tier';
    if (tier === 'TIER5') return uiLanguage === 'ko' ? 'Tier 5 (프로) 멤버십' : 'Tier 5 (Expert) Tier';
    return tier;
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fadeIn">
      <div className="relative w-full max-w-lg rounded-3xl border border-white/10 bg-[#0e0e11] text-white p-6 shadow-2xl space-y-6 overflow-hidden">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 p-1 text-white/40 hover:text-white/80 transition-colors rounded-lg bg-white/5"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-rose-400">
            <ShieldAlert className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-base font-bold tracking-tight">
              {uiLanguage === 'ko' ? `${getProductName()} 해지 / 환불` : `Cancel ${getProductName()}`}
            </h3>
            <p className="text-xs text-white/40">
              {uiLanguage === 'ko' ? '구독 해지 방식 및 차액 환불 금액을 확인해 주세요.' : 'Review cancellation options and refund calculation.'}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="py-12 flex flex-col items-center justify-center gap-3">
            <RefreshCw className="w-6 h-6 text-emerald-400 animate-spin" />
            <p className="text-xs text-white/50">{uiLanguage === 'ko' ? '환불 금액 일할 계산 중...' : 'Calculating prorated refund...'}</p>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Options Selection */}
            <div className="space-y-3">
              <label className="text-xs font-bold text-white/70 block">
                {uiLanguage === 'ko' ? '1. 해지 방식 선택' : '1. Choose Cancellation Type'}
              </label>

              {/* Option 1: Prorated Immediate Refund */}
              <div
                onClick={() => setCancelType('IMMEDIATE_REFUND')}
                className={`p-4 rounded-2xl border cursor-pointer transition-all ${cancelType === 'IMMEDIATE_REFUND' ? 'bg-rose-500/10 border-rose-500/40 text-white' : 'bg-white/[0.02] border-white/5 text-white/60 hover:border-white/10'}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-black text-xs text-rose-400 flex items-center gap-1.5">
                    ⚡ {uiLanguage === 'ko' ? '중도 해지 및 차액 환불 (즉시 권한 회수)' : 'Immediate Refund & Cancel'}
                  </span>
                  {cancelType === 'IMMEDIATE_REFUND' && <CheckCircle2 className="w-4 h-4 text-rose-400" />}
                </div>
                <p className="text-[11px] text-white/50 leading-relaxed mb-3">
                  {uiLanguage === 'ko'
                    ? '이용하신 기간(일수)을 제외한 남은 기간만큼 차액을 카드 결제 취소로 즉시 환불합니다.'
                    : 'Unused days will be calculated and refunded to your payment card immediately.'}
                </p>

                {preview && (
                  <div className="p-3 bg-black/40 rounded-xl border border-white/5 space-y-1.5 text-xs">
                    <div className="flex justify-between text-white/50">
                      <span>{uiLanguage === 'ko' ? '원 결제 금액' : 'Original Amount'}</span>
                      <span className="font-mono">₩{preview.totalAmount?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-white/50">
                      <span>{uiLanguage === 'ko' ? '이용 기간 / 잔여 기간' : 'Used / Remaining Days'}</span>
                      <span className="font-mono">{preview.elapsedDays}일 사용 / {preview.remainingDays}일 남음</span>
                    </div>
                    <div className="flex justify-between items-center pt-1 border-t border-white/10 font-bold">
                      <span className="text-rose-300">{uiLanguage === 'ko' ? '최종 환불 예정 금액' : 'Estimated Refund'}</span>
                      <span className="text-sm font-mono font-black text-rose-400">
                        ₩{preview.refundAmount?.toLocaleString()}
                        {preview.isFullRefund && <span className="ml-1 text-[10px] text-emerald-400 font-bold">(100% 전액환불)</span>}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Option 2: Turn Off Auto-Renewal */}
              <div
                onClick={() => setCancelType('AUTO_RENEW_OFF')}
                className={`p-4 rounded-2xl border cursor-pointer transition-all ${cancelType === 'AUTO_RENEW_OFF' ? 'bg-amber-500/10 border-amber-500/40 text-white' : 'bg-white/[0.02] border-white/5 text-white/60 hover:border-white/10'}`}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <span className="font-black text-xs text-amber-400 flex items-center gap-1.5">
                    🔒 {uiLanguage === 'ko' ? '정기 결제만 해지 (만료일까지 혜택 유지)' : 'Turn Off Auto-Renewal Only'}
                  </span>
                  {cancelType === 'AUTO_RENEW_OFF' && <CheckCircle2 className="w-4 h-4 text-amber-400" />}
                </div>
                <p className="text-[11px] text-white/50 leading-relaxed">
                  {uiLanguage === 'ko'
                    ? `다음 달 자동 결제만 해지하며, 이번 달 남은 기간(${preview?.expiresAt?.slice(0, 10) || '만료일'}) 동안은 혜택을 계속 이용합니다.`
                    : 'Prevents next auto-charge. Current benefits remain active until expiry.'}
                </p>
              </div>
            </div>

            {/* Optional Reason Input */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-white/60 block">
                {uiLanguage === 'ko' ? '해지 사유 (선택)' : 'Reason (Optional)'}
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={uiLanguage === 'ko' ? '해지 사유를 알려주시면 서비스 개선에 반영하겠습니다.' : 'Tell us why you are leaving...'}
                className="w-full px-3.5 py-2 rounded-xl bg-white/[0.03] border border-white/10 text-xs text-white placeholder-white/20 outline-none focus:border-rose-500/50 transition-all"
              />
            </div>

            {/* Refund Terms Collapsible Section */}
            <CollapsibleSection
              title={uiLanguage === 'ko' ? '환불 정책 및 유의사항 상세' : 'Refund Policy Details'}
              subtitle={uiLanguage === 'ko' ? '전자상거래법 환불 기준 및 카드사 처리 소요 기간 안내' : 'Terms and card refund processing time'}
              icon={<AlertCircle className="w-4 h-4 text-amber-400" />}
              isOpen={isNoticeOpen}
              onToggle={() => setIsNoticeOpen(!isNoticeOpen)}
              variant="ghost"
            >
              <ul className="text-[11px] text-white/50 space-y-1.5 list-disc pl-4">
                <li>{uiLanguage === 'ko' ? '결제 후 7일 이내 및 사용 이력이 없는 경우 100% 전액 환불 처리됩니다.' : 'Full refund within 7 days if unused.'}</li>
                <li>{uiLanguage === 'ko' ? '7일 경과 후 중도 해지 시 이용 일수만큼 차감 후 잔여 금액이 카드사로 환불됩니다.' : 'Prorated deduction applies after 7 days.'}</li>
                <li>{uiLanguage === 'ko' ? '카드사 영업일 기준 3~5일 이내에 승인 취소가 완료됩니다.' : 'Card refunds take 3-5 business days.'}</li>
              </ul>
            </CollapsibleSection>
          </div>
        )}

        {/* Footer Actions */}
        <div className="flex gap-3 pt-2 border-t border-white/5">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 py-2.5 bg-white/5 hover:bg-white/10 text-white/70 text-xs font-bold rounded-xl transition-all"
          >
            {uiLanguage === 'ko' ? '취소' : 'Close'}
          </button>
          <button
            type="button"
            onClick={handleExecuteCancel}
            disabled={loading || isSubmitting}
            className={`flex-1 py-2.5 text-xs font-black rounded-xl transition-all flex items-center justify-center gap-1.5 shadow-lg ${cancelType === 'IMMEDIATE_REFUND' ? 'bg-rose-500 hover:bg-rose-600 text-white shadow-rose-500/20' : 'bg-amber-500 hover:bg-amber-600 text-black shadow-amber-500/20'}`}
          >
            {isSubmitting ? (
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
            ) : cancelType === 'IMMEDIATE_REFUND' ? (
              uiLanguage === 'ko' ? '중도 해지 및 환불받기' : 'Cancel & Process Refund'
            ) : (
              uiLanguage === 'ko' ? '정기 결제 해지하기' : 'Disable Auto-Renewal'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
